import React, { useState } from 'react'

import { Button, Grid, Typography } from '@mui/material'
import { useModal } from '../../../hooks/useModal'
import { Modal } from '../../../components/_EOS/Modal'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { RemoveOrderFromWorkOrder } from './RemoveOrderFromWorkOrder'
import { useApi } from '../../../hooks/useApi'
import { WorkOrderOutbound } from '../../../interfaces/WorkOrderOutbound'
import { Status } from '../../../hooks/useFetchState'
import axios from 'axios'
import { ApiConfig } from '../../../config/api'
import { ChangeWorkOrderTagComponent } from './ChangeWorkOrderTagComponent'
import { ChangeWorkOrderWarehouseComponent } from './ChangeWorkOrderWarehouseComponent'
import { t } from '../../../intl/resources'

interface EditWorkOrderModalProps {
  workOrder: WorkOrderOutbound
}

const putWorkOrder = (workOrderId: number, ordersToRemove: number[]) => {
  const url = new URL(`${ApiConfig.WorkOrderOutboundApi}`)

  return axios.put(url.toString(), {
    workOrderId,
    workOrderDeleteDetails: ordersToRemove.map((item) => ({ orderId: item }))
  })
}

export const EditWorkOrderModal: React.FC<EditWorkOrderModalProps> = ({
  workOrder
}) => {
  if (!workOrder) {
    return <Typography>No se ha seleccionado una orden de trabajo</Typography>
  }

  const { isOpen, openModal, size, closeModal, title } =
    useModal('Quitar pedidos')

  const { openSnackBar, isActive, isSuccess, message } = useSnackbar()

  // Remove Orders
  const [selectedOrdersToRemove, setSelectedOrdersToRevemo] = useState<
    number[]
  >([])

  const handleChangeOrdersToRemove = (orders: number[]) => {
    setSelectedOrdersToRevemo(orders)
  }

  const { state: updateWorkOrderState, callApi: updateWorkOrderCallApi } =
    useApi<WorkOrderOutbound>()

  const confirmRemoveOrderIsOpen = useModal('Confirmar')

  const requestRemoveOrdersFromWorkOrder = () => {
    updateWorkOrderCallApi(putWorkOrder(workOrder.id, selectedOrdersToRemove))
      .then(() => {
        openSnackBar(
          `Se han quitado correctamente los pedidos desde la orden de tabajo ${workOrder.id}`,
          true
        )
        closeModal()
        confirmRemoveOrderIsOpen.closeModal()
      })
      .catch((err) => openSnackBar(err, false))
  }

  const isLoading = false

  if (!workOrder) {
    return (
      <Typography>No se ha seleccionado ninguna orden de trabajo.</Typography>
    )
  }

  return (
    <Grid container direction='column' spacing={3}>
      <ChangeWorkOrderTagComponent
        actualTag={workOrder.workOrderTag}
        workOrderIds={[workOrder.id]}
        openSnackBar={openSnackBar}
      />
      <ChangeWorkOrderWarehouseComponent
        workOrderId={workOrder.id}
        actualWarehouseId={workOrder.warehouseId}
        openSnackBar={openSnackBar}
      />
      <Grid container direction='row' item xs={12} spacing={3}>
        <Grid item xs={12} sm={4}>
          <Typography>Quitar pedidos</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            variant='contained'
            color='primary'
            onClick={() => openModal('md')}
            disabled={isLoading}
          >
            {t('SELECT')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Modal
            title={title}
            open={isOpen}
            maxWidth={size}
            cancelButton={{
              title: t('EXIT'),
              action: closeModal
            }}
            successButton={{
              title: t('REMOVE'),
              action: () => confirmRemoveOrderIsOpen.openModal('sm')
            }}
            onClose={closeModal}
          >
            <RemoveOrderFromWorkOrder
              workOrderId={workOrder.id as number}
              ordersToRemove={selectedOrdersToRemove}
              setSelectedOrdersToRemove={handleChangeOrdersToRemove}
              loading={updateWorkOrderState.status === Status.Loading}
            />
          </Modal>
          <Modal
            title='Quitar Pedidos'
            open={confirmRemoveOrderIsOpen.isOpen}
            maxWidth={confirmRemoveOrderIsOpen.size}
            cancelButton={{
              title: t('CANCEL'),
              action: confirmRemoveOrderIsOpen.closeModal
            }}
            successButton={{
              title: t('ACCEPT'),
              action: requestRemoveOrdersFromWorkOrder
            }}
            loading={updateWorkOrderState.status === Status.Loading}
            onClose={confirmRemoveOrderIsOpen.closeModal}
          >
            <Typography variant='body2' component={'h2'}>
              ¿Está seguro que desea quitar los pedidos?
            </Typography>
          </Modal>
        </Grid>
        <Grid item xs={12}>
          <SnackBar
            message={message}
            open={isActive}
            status={isSuccess ? 'success' : 'error'}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
interface EditWorkOrderModalComponentProps {
  editModal: any
  requestWorkOrderOutbounds: () => void
}

export const EditWorkOrderModalComponent: React.FC<EditWorkOrderModalComponentProps> =
  ({ editModal, requestWorkOrderOutbounds }) => {
    return (
      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        onClose={() => {
          editModal.closeModal()
          requestWorkOrderOutbounds()
        }}
        title={editModal.title}
        cancelButton={{
          title: t('EXIT'),
          action: () => {
            editModal.closeModal()
            requestWorkOrderOutbounds()
          }
        }}
      >
        {editModal.extendedProps && (
          <EditWorkOrderModal workOrder={editModal.extendedProps?.workOrder} />
        )}
      </Modal>
    )
  }
